/*
 * @providesModule SKZPlayerLevel
 * 
 */
import SkillzModule from 'SkillzModule';
import _ from 'lodash';
import { UserTicketzInfo } from './SKZTicketz';

const TICKETZ_ID = "-1";
const MAX_LEVEL = 100;

interface IPlayerLevelRecord {
  id?: number;
  experience: number;
  experience_to_next_level: number;
  experience_of_previous_level: number;
  current_level: number;
  system_wide: boolean;
  last_seen_level: number;
  game_icon_url: string;
  game_id: string;
  game_name: string;
  experience_to_keep_level: number;
}

export default class SKZPlayerLevel implements IPlayerLevelRecord {

  id?: number;
  experience: number;
  experience_to_next_level: number;
  experience_of_previous_level: number;
  current_level: number;
  system_wide: boolean;
  last_seen_level: number;
  game_icon_url: string;
  game_id: string;
  game_name: string;
  experience_to_keep_level: number;

  constructor(keyValueSource: IPlayerLevelRecord) {
    this.id = keyValueSource.id;
    this.experience = keyValueSource.experience;
    this.experience_to_next_level = keyValueSource.experience_to_next_level;
    this.experience_of_previous_level = keyValueSource.experience_of_previous_level;
    this.current_level = keyValueSource.current_level;
    this.system_wide = keyValueSource.system_wide;
    this.last_seen_level = keyValueSource.last_seen_level;
    this.game_icon_url = keyValueSource.game_icon_url;
    this.game_id = keyValueSource.game_id;
    this.game_name = keyValueSource.game_name;
    this.experience_to_keep_level = keyValueSource.experience_to_keep_level;

    return this;
  }

  static make(levels: Array<IPlayerLevelRecord>): Array<SKZPlayerLevel> {
    if (Array.isArray(levels)) {
      return _.map(
        levels,
        (levelRecord: IPlayerLevelRecord): SKZPlayerLevel => new SKZPlayerLevel(levelRecord)
      );
    }
    return [];
  }

  static createFromTicketzInfo(
    ticketzToKeepTier: number,
    ticketzInfo: UserTicketzInfo
  ): SKZPlayerLevel {
    const playerLevelForm = {
      id: undefined,
      experience: ticketzInfo.balance,
      experience_to_next_level: ticketzInfo.goal + (ticketzInfo.balance - ticketzInfo.progress),
      experience_of_previous_level: ticketzInfo.balance - ticketzInfo.progress,
      current_level: ticketzInfo.multiplier,
      system_wide: false,
      last_seen_level: ticketzInfo.multiplier,
      game_icon_url: 'ic_ticketz_background.png',
      game_id: TICKETZ_ID,
      game_name: 'Ticketz Tier',
      experience_to_keep_level: ticketzToKeepTier,
    };
    return new SKZPlayerLevel(playerLevelForm);
  }

  isGrandFathered(): boolean {
    return this.last_seen_level == 1 && this.current_level > 2;
  }

  isSkillzLevel(): boolean {
    return this.system_wide;
  }

  isCurrentGameLevel(): boolean {
    return !this.system_wide && this.game_id == SkillzModule.App.GameId;
  }

  isTicketzLevel(): boolean {
    return !this.system_wide && this.game_id == TICKETZ_ID;
  }

  isMaxLevel(): boolean {
    return this.current_level == MAX_LEVEL;
  }

  canMarkAsSeen(): boolean {
    return (
      (this.isSkillzLevel() || this.isCurrentGameLevel()) &&
      this.current_level != this.last_seen_level
    );
  }

  getExperienceToNextLevel(): number {
    const xp = this.experience_to_next_level - this.experience;
    return xp <= 0 ? 0 : xp;
  }

  getTotalExperienceToNextLevel(): number {
    return this.experience_to_next_level - this.experience_of_previous_level;
  }

  getCurrentXp(): number {
    return this.experience - this.experience_of_previous_level;
  }

  getPercentageToNextLevel(): number {
    if (this.isMaxLevel()) {
      return 99;
    }

    const currentXp = this.getCurrentXp();
    let xpToNextLevel = this.getTotalExperienceToNextLevel();

    if (xpToNextLevel == 0 && this.experience_to_keep_level > 0) {
      xpToNextLevel = this.experience_to_keep_level;
    }

    let percent = currentXp >= xpToNextLevel ? 99 : (currentXp / xpToNextLevel) * 100; // eslint-disable-line no-magic-numbers
    if (!percent || !isFinite(percent)) {
      percent = 0;
    }

    return percent;
  }
}
